import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowRight, Search, Users, Cpu, Sprout, Plus } from "lucide-react";
import SEOMeta from "../components/SEOMeta";
import { Reveal, Chapter, MaskedLine } from "../components/Reveal";
import { useContent, useBusinesses } from "../lib/content";

const HERO_IMG =
  "https://images.unsplash.com/photo-1462556791646-c201b8241a94?crop=entropy&cs=srgb&fm=jpg&q=85";
const APPROACH_IMG =
  "https://images.pexels.com/photos/30715673/pexels-photo-30715673.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const APPROACH_ICONS = [Search, Users, Cpu, Sprout];
const MARQUEE_WORDS = ["Innovation", "Integrity", "Customer Focus", "Responsible Growth", "Long-Term Value", "Adaptability"];

const VentureCard = ({ biz, large }) => (
  <div
    data-testid={`venture-card-${biz.id}`}
    className={`group relative overflow-hidden border border-white/10 hover:border-gold/50 transition-colors duration-500 ${
      large ? "md:col-span-2 min-h-[320px] md:min-h-[420px]" : "min-h-[320px]"
    }`}
  >
    {biz.image_url && (
      <img
        src={biz.image_url}
        alt={biz.name}
        className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-65 group-hover:scale-105 transition-[transform,opacity] duration-700"
      />
    )}
    <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/50 to-transparent" />
    <div className="relative h-full flex flex-col justify-end p-8">
      <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold mb-3">{biz.category}</p>
      <h3 className="font-display text-2xl sm:text-3xl text-white mb-2">{biz.name}</h3>
      <p className="text-sm text-white/60 max-w-md line-clamp-2">{biz.description}</p>
      <span className="mt-4 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase text-white/70 group-hover:text-gold transition-colors duration-300">
        Learn More <ArrowRight size={12} />
      </span>
    </div>
  </div>
);

export default function Home() {
  const content = useContent();
  const businesses = useBusinesses();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <div>
      <SEOMeta
        title="Touson Ventures (OPC) Private Limited | Building Businesses. Creating Possibilities."
        description="Touson Ventures is a diversified Indian business company based in Pune, Maharashtra — building practical, innovative and sustainable businesses for a changing world."
      />

      {/* Hero */}
      <section ref={heroRef} data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden noise-overlay">
        <motion.div style={{ y: bgY }} className="absolute inset-0">
          <img src={HERO_IMG} alt="Modern glass architecture" className="w-full h-[115%] object-cover opacity-45" />
          <div className="absolute inset-0 bg-gradient-to-b from-navy/80 via-navy/40 to-navy" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy/70 via-transparent to-transparent" />
        </motion.div>

        <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-6 sm:px-10 pb-28 sm:pb-36 pt-44 w-full">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3, duration: 1 }}
            className="font-mono text-[10px] sm:text-xs tracking-[0.35em] text-gold mb-8"
            data-testid="hero-eyebrow"
          >
            TOUSON VENTURES (OPC) PRIVATE LIMITED — PUNE, INDIA
          </motion.p>
          <h1
            data-testid="hero-headline"
            className="font-display tracking-tight text-white text-5xl sm:text-7xl lg:text-8xl leading-[1.02]"
          >
            <MaskedLine delay={0.15}>{content.home.hero_line_1}</MaskedLine>
            <MaskedLine delay={0.32} className="gold-gradient-text italic">
              {content.home.hero_line_2}
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 max-w-xl text-white/70 text-base sm:text-lg leading-relaxed"
            data-testid="hero-subtext"
          >
            {content.home.hero_subtext}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link
              to="/businesses"
              data-testid="hero-explore-button"
              className="group inline-flex items-center gap-3 bg-gold text-navy font-medium text-sm px-8 py-4 rounded-full hover:bg-gold-light transition-colors duration-300"
            >
              Explore Our Businesses
              <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-contact-button"
              className="inline-flex items-center gap-3 border border-white/25 text-white font-medium text-sm px-8 py-4 rounded-full hover:border-gold hover:text-gold transition-colors duration-300"
            >
              Contact Us
            </Link>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.8, duration: 1 }}
          className="absolute bottom-8 right-8 hidden md:flex flex-col items-center gap-3 z-10"
        >
          <span className="font-mono text-[9px] tracking-[0.4em] text-white/40 uppercase" style={{ writingMode: "vertical-rl" }}>
            Scroll
          </span>
          <div className="w-px h-12 bg-gradient-to-b from-gold to-transparent" />
        </motion.div>
      </section>

      {/* Marquee */}
      <section data-testid="home-marquee" className="border-y border-white/5 py-8 sm:py-10 overflow-hidden">
        <Marquee speed={25} gradient={false}>
          {MARQUEE_WORDS.map((w) => (
            <span key={w} className="flex items-center">
              <span className="font-display italic text-2xl sm:text-3xl text-white/20 px-8">{w}</span>
              <span className="w-1.5 h-1.5 bg-gold/50 rotate-45" />
            </span>
          ))}
        </Marquee>
      </section>

      {/* 01 — Who We Are */}
      <section data-testid="home-who-section" className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <Chapter num="01" title={content.home.who_title} testId="home-who-chapter" />
              <p className="text-white/65 text-base sm:text-lg leading-relaxed max-w-2xl" data-testid="home-who-text">
                {content.home.who_text}
              </p>
              <Link
                to="/about"
                data-testid="home-who-more-link"
                className="mt-8 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] uppercase text-gold hover:text-gold-light transition-colors duration-300"
              >
                More About Us <ArrowRight size={13} />
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <div className="relative overflow-hidden border border-white/10">
                <img src={APPROACH_IMG} alt="Abstract architectural texture with gold accents" className="w-full h-[380px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/60 to-transparent" />
                <div className="absolute bottom-5 left-5 font-mono text-[10px] tracking-[0.3em] text-white/60 uppercase">
                  Pune · Maharashtra · India
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 02 — Our Approach */}
      <section data-testid="home-approach-section" className="py-24 sm:py-32 bg-navy-800/40">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <Reveal>
            <Chapter num="02" title="Our Approach" testId="home-approach-chapter" />
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {content.home.approach_items.map((item, i) => {
              const Icon = APPROACH_ICONS[i % APPROACH_ICONS.length];
              return (
                <Reveal key={item.title} delay={i * 0.08}>
                  <div
                    data-testid={`home-approach-item-${i}`}
                    className="group h-full bg-navy p-8 sm:p-10 hover:bg-navy-700 transition-colors duration-500"
                  >
                    <Icon size={22} className="text-gold mb-8" strokeWidth={1.5} />
                    <p className="font-mono text-[10px] tracking-[0.3em] text-white/40 mb-3">
                      {String(i + 1).padStart(2, "0")}
                    </p>
                    <h3 className="font-display text-xl text-white mb-3">{item.title}</h3>
                    <p className="text-sm text-white/55 leading-relaxed">{item.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={0.2}>
            <Link
              to="/approach"
              data-testid="home-approach-more-link"
              className="mt-10 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] uppercase text-gold hover:text-gold-light transition-colors duration-300"
            >
              Our Full Approach <ArrowRight size={13} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* 03 — Businesses & Opportunities */}
      <section data-testid="home-businesses-section" className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-6">
              <Chapter num="03" title="Businesses & Opportunities" testId="home-businesses-chapter" />
              <Link
                to="/businesses"
                data-testid="home-businesses-viewall-link"
                className="mb-10 sm:mb-14 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] uppercase text-white/60 hover:text-gold transition-colors duration-300"
              >
                View All <ArrowUpRight size={13} />
              </Link>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {businesses.slice(0, 3).map((biz, i) => (
              <Reveal key={biz.id} delay={i * 0.1} className={i === 0 ? "md:col-span-2" : ""}>
                <Link to="/businesses" className="block h-full">
                  <VentureCard biz={biz} large={i === 0} />
                </Link>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <Link to="/contact" data-testid="home-newventure-card" className="block h-full">
                <div className="h-full min-h-[320px] border border-dashed border-white/15 hover:border-gold/50 transition-colors duration-500 flex flex-col items-center justify-center gap-4 p-8 text-center group">
                  <span className="w-12 h-12 rounded-full border border-gold/40 flex items-center justify-center text-gold group-hover:bg-gold group-hover:text-navy transition-colors duration-300">
                    <Plus size={18} />
                  </span>
                  <p className="font-display text-xl text-white/80">New ventures in development</p>
                  <p className="text-sm text-white/45 max-w-[240px]">
                    Have an opportunity to discuss? We would like to hear it.
                  </p>
                </div>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 04 — Why Touson */}
      <section data-testid="home-why-section" className="py-24 sm:py-32 bg-navy-800/40">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <Reveal>
              <Chapter num="04" title="Why Touson" testId="home-why-chapter" />
              <p className="font-display text-2xl sm:text-3xl text-white/85 leading-snug">
                Principles that guide every venture we build —{" "}
                <span className="gold-gradient-text italic">today and for the long term.</span>
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            {content.home.why_items.map((item, i) => (
              <Reveal key={item.title} delay={i * 0.06}>
                <div
                  data-testid={`home-why-item-${i}`}
                  className="group grid sm:grid-cols-12 gap-2 sm:gap-6 py-7 border-t border-white/10 last:border-b items-baseline"
                >
                  <span className="sm:col-span-2 font-mono text-sm text-gold">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="sm:col-span-4 font-display text-xl text-white group-hover:text-gold transition-colors duration-300">
                    {item.title}
                  </h3>
                  <p className="sm:col-span-6 text-sm text-white/55 leading-relaxed">{item.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 05 — Contact CTA */}
      <section data-testid="home-cta-section" className="py-28 sm:py-40 relative overflow-hidden noise-overlay">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.08),transparent_60%)]" />
        <div className="relative max-w-4xl mx-auto px-6 sm:px-10 text-center">
          <Reveal>
            <p className="font-mono text-[10px] tracking-[0.35em] text-gold uppercase mb-8">05 — Contact Us</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-tight" data-testid="home-cta-title">
              {content.home.cta_title}
            </h2>
            <p className="mt-6 text-white/60 text-base sm:text-lg max-w-xl mx-auto" data-testid="home-cta-text">
              {content.home.cta_text}
            </p>
            <Link
              to="/contact"
              data-testid="home-cta-button"
              className="group mt-10 inline-flex items-center gap-3 bg-gold text-navy font-medium text-sm px-10 py-4 rounded-full hover:bg-gold-light transition-colors duration-300"
            >
              Start a Conversation
              <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
